import { z } from "zod";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import {
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useAppForm } from "@/hooks/useForm";
import type { Empresa } from "@/@types/Api_Extracted";
import { useApi } from "@/hooks/useApi";

interface EmpresaDialogProps {
	readonly onSuccess: () => void;
	readonly empresa: Empresa;
}
export function DialogEditEmpresa(props: EmpresaDialogProps) {
	const api = useApi();
	const mutation = api.useMutation("put", "/v1/Admin/Empresas/{idEmpresa}");
	const form = useAppForm({
		defaultValues: {
			...props.empresa,
			password: "",
		} satisfies FormSchema as FormSchema,
		validators: {
			onChange: FormSchema,
		},
		onSubmit: async (data) => {
			try {
				await mutation.mutateAsync({
					params: {
						path: { idEmpresa: props.empresa.idUsuario },
					},
					body: data.value,
				});
				toast("Datos guardados.", { type: "success" });
				props.onSuccess();
			} catch (err) {
				console.error("Error al guardar los datos:", err);
				toast("Error al guardar los datos.", { type: "error" });
			}
		},
	});

	return (
		<DialogContent
			className="sm:max-w-[425px]"
			description="Modal para editar una empresa"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void form.handleSubmit(e);
				}}
				className="space-y-8"
			>
				<DialogHeader>
					<DialogTitle>Editar Empresa</DialogTitle>
				</DialogHeader>
				{/* <SelectControl control={form.control} name="estado" label="Estado" /> */}

				<form.AppField
					name="estado"
					children={(field) => (
						<field.SelectField
							label="Estado"
							options={{
								PENDING: localizedStatus.PENDING,
								ACTIVE: localizedStatus.ACTIVE,
								INACTIVE: localizedStatus.INACTIVE,
							}}
						/>
					)}
				/>

				<form.AppField
					name="razonSocial"
					children={(field) => (
						<field.TextField
							label="Razón Social"
							placeholder="Razón Social"
							type="text"
							autoComplete="organization-name"
						/>
					)}
				/>
				<form.AppField
					name="username"
					children={(field) => (
						<field.TextField
							label="Correo Electrónico"
							placeholder="Correo Electrónico"
							type="email"
							autoComplete="email"
						/>
					)}
				/>
				<form.AppField
					name="password"
					children={(field) => (
						<field.TextField
							label="Contraseña"
							placeholder="Contraseña"
							type="password"
							autoComplete="new-password"
						/>
					)}
				/>
				<form.AppField
					name="personaContacto"
					children={(field) => (
						<field.TextField
							label="Persona de contacto"
							placeholder="Nombre"
							type="text"
							autoCapitalize="name"
						/>
					)}
				/>
				<form.AppField
					name="telefonoContacto"
					children={(field) => (
						<field.TextField
							label="Número de contacto"
							placeholder="Teléfono"
							type="tel"
							autoComplete="tel"
						/>
					)}
				/>
				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline" type="button">
							Cancelar
						</Button>
					</DialogClose>

					<Button type="submit">Save changes</Button>
				</DialogFooter>
			</form>
		</DialogContent>
	);
}

const FormSchema = z.object({
	idUsuario: z.number(),
	estado: z.enum(["PENDING", "ACTIVE", "INACTIVE"]),
	razonSocial: z
		.string({
			error: "La razón social es obligatoria.",
		})
		.min(2, {
			message: "La razón social debe tener al menos 2 caracteres.",
		}),
	username: z
		.email({
			message: "El correo electrónico debe ser válido.",
		})
		.transform((email) => email.toLowerCase()),
	password: z
		.string({
			error: "La contraseña es obligatoria.",
		})
		.regex(/(^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$)|^$/, {
			message:
				"La contraseña debe tener al menos 8 caracteres, una letra mayúscula, una letra minúscula y un número.",
		}),
	personaContacto: z
		.string({
			error: "El nombre de contacto es obligatorio.",
		})
		.min(2, {
			message: "El nombre de contacto debe tener al menos 2 caracteres.",
		}),
	telefonoContacto: z
		.string({
			error: "El número de contacto es obligatorio.",
		})
		.regex(/^(\+\d{1,3})?(\d{6,12})$/, {
			message:
				"El número de contacto debe de ser un número de teléfono válido.",
		}),
});
type FormSchema = z.infer<typeof FormSchema>;
const localizedStatus = {
	PENDING: "Pendiente",
	ACTIVE: "Activo",
	INACTIVE: "Inactivo",
};
